import React, { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from '../icons';
import { blobHolen } from '../../services/download';
import './Vorschaubild.css';

/**
 * Kleines Vorschaubild eines Mediums (Backend: GET /api/media-assets/:id/vorschau,
 * JPEG hoechstens 480 px breit). <img> kann keinen Authorization-Header
 * senden: das Bild kommt mit Bearer als Blob und wird ueber eine Objekt-URL
 * gezeigt (wie MediaPreview).
 *
 * - laedt erst, wenn die Kachel in die Naehe des sichtbaren Bereichs kommt
 *   (IntersectionObserver; ohne ihn, z. B. in jsdom, sofort),
 * - merkt sich geladene Bilder modulweit, damit Listen beim Neuzeichnen und
 *   Seitenwechsel nicht neu laden,
 * - zeigt bis dahin und ohne Vorschaubild (404) ein Symbol je Medienart.
 */

export type VorschauGroesse = number | 's' | 'm' | 'l';

const GROESSEN: Record<'s' | 'm' | 'l', number> = { s: 48, m: 72, l: 160 };

const SYMBOL: Record<string, IconName> = {
  video: 'play',
  pdf: 'file-text',
  image: 'image',
  document: 'file-check',
};

/** Hoechstzahl gemerkter Objekt-URLs; die aeltesten werden freigegeben. */
const MAX_GEMERKT = 400;
const gemerkt = new Map<string, string>();
/** Ohne Vorschaubild (404 oder Fehler): in dieser Sitzung nicht erneut holen. */
const ohneBild = new Set<string>();
const laufend = new Map<string, Promise<string | null>>();

function merken(assetId: string, url: string): void {
  gemerkt.set(assetId, url);
  while (gemerkt.size > MAX_GEMERKT) {
    const [aeltester, alteUrl] = gemerkt.entries().next().value as [string, string];
    gemerkt.delete(aeltester);
    URL.revokeObjectURL(alteUrl);
  }
}

/** Objekt-URL des Vorschaubilds oder null (keins vorhanden / Fehler). */
export function vorschauUrlHolen(assetId: string): Promise<string | null> {
  const bekannt = gemerkt.get(assetId);
  if (bekannt) return Promise.resolve(bekannt);
  if (ohneBild.has(assetId)) return Promise.resolve(null);
  const offen = laufend.get(assetId);
  if (offen) return offen;
  const auftrag = blobHolen(`/media-assets/${encodeURIComponent(assetId)}/vorschau`)
    .then((blob) => {
      const url = URL.createObjectURL(new Blob([blob], { type: 'image/jpeg' }));
      merken(assetId, url);
      return url;
    })
    .catch(() => {
      ohneBild.add(assetId);
      return null;
    })
    .finally(() => laufend.delete(assetId));
  laufend.set(assetId, auftrag);
  return auftrag;
}

/** Gemerktes Vorschaubild vergessen (nach dem Neuerzeugen); ohne Id alle. */
export function vorschauVergessen(assetId?: string): void {
  const ids = assetId ? [assetId] : Array.from(new Set([...gemerkt.keys(), ...ohneBild]));
  for (const id of ids) {
    const url = gemerkt.get(id);
    if (url) URL.revokeObjectURL(url);
    gemerkt.delete(id);
    ohneBild.delete(id);
  }
}

export interface VorschaubildProps {
  assetId?: string | null;
  /** video | pdf | image | document: bestimmt das Symbol. */
  kind?: string | null;
  /** false = es gibt (noch) kein Vorschaubild, gar nicht erst anfragen. */
  vorschau?: boolean;
  /** Kantenlaenge in px oder s/m/l (48/72/160). */
  groesse?: VorschauGroesse;
  alt: string;
  /** Hochzaehlen erzwingt neues Laden (nach dem Neuerzeugen). */
  version?: number;
  className?: string;
}

export const Vorschaubild: React.FC<VorschaubildProps> = ({
  assetId, kind, vorschau, groesse = 'm', alt, version = 0, className,
}) => {
  const px = typeof groesse === 'number' ? groesse : GROESSEN[groesse];
  const kasten = useRef<HTMLSpanElement>(null);
  const [sichtbar, setSichtbar] = useState(() => typeof IntersectionObserver === 'undefined');
  const [url, setUrl] = useState<string | null>(() => (assetId ? gemerkt.get(assetId) ?? null : null));

  useEffect(() => {
    if (sichtbar || !kasten.current) return undefined;
    const beobachter = new IntersectionObserver((eintraege) => {
      if (eintraege.some((e) => e.isIntersecting)) {
        setSichtbar(true);
        beobachter.disconnect();
      }
    }, { rootMargin: '200px' });
    beobachter.observe(kasten.current);
    return () => beobachter.disconnect();
  }, [sichtbar]);

  useEffect(() => {
    if (!assetId || vorschau === false) {
      setUrl(null);
      return undefined;
    }
    const bekannt = gemerkt.get(assetId);
    if (bekannt) {
      setUrl(bekannt);
      return undefined;
    }
    setUrl(null);
    if (!sichtbar) return undefined;
    let aktiv = true;
    vorschauUrlHolen(assetId).then((neu) => { if (aktiv) setUrl(neu); });
    return () => { aktiv = false; };
  }, [assetId, vorschau, sichtbar, version]);

  const symbol = SYMBOL[kind ?? ''] ?? 'image';
  return (
    <span
      ref={kasten}
      className={['vorschaubild', url ? 'vorschaubild--bild' : 'vorschaubild--symbol', className].filter(Boolean).join(' ')}
      style={{ width: px, height: px }}
      data-kind={kind ?? undefined}
    >
      {url ? (
        <img src={url} alt={alt} className="vorschaubild__bild" />
      ) : (
        <span className="vorschaubild__platzhalter" role="img" aria-label={alt}>
          <Icon name={symbol} size={Math.max(16, Math.round(px * 0.38))} />
        </span>
      )}
    </span>
  );
};

export default Vorschaubild;
